import { Txt } from "./text";
import { Dom } from "./dom";

export class Dialog
{
    readonly dialog: HTMLDivElement;
    readonly body: HTMLDivElement;
    readonly title: HTMLHeadingElement;
    readonly closeButton: HTMLButtonElement;

    private readonly preventBackdropClose: boolean;
    private readonly element: HTMLDialogElement;
    private readonly shownCallbacks: (() => void)[] = [];
    private clickHandler?: (e: MouseEvent) => void;
    private mouseDownHandler?: (e: MouseEvent) => void;
    private mouseDownOnBackdrop = false;

    constructor(title: string, preventBackdropClose = false)
    {
        this.preventBackdropClose = preventBackdropClose;

        this.element = Dom.create("dialog", "w-dialog", "w-dialog-fade");
        this.element.setAttribute("role", "dialog");
        this.element.addEventListener("cancel", e =>
        {
            e.preventDefault();
            if (!this.preventBackdropClose)
            {
                this.hide();
            }
        });

        this.dialog = Dom.addDiv(this.element, "w-dialog-box");
        const content = Dom.addDiv(this.dialog, "w-dialog-content");
        const header = Dom.addDiv(content, "w-dialog-header");
        this.body = Dom.addDiv(content, "w-dialog-body");

        this.closeButton = Dom.add("button", header, "w-dialog-close");
        this.closeButton.type = "button";
        this.closeButton.setAttribute("data-w-dialog-dismiss", "");
        this.closeButton.innerHTML = "&times;";

        this.title = Dom.add("h4", header, "w-dialog-title");
        this.title.textContent = title || Txt.changeForm;

        document.body.appendChild(this.element);
    }

    show(): void
    {
        if (this.element.open)
        {
            return;
        }

        if (!this.element.isConnected)
        {
            document.body.appendChild(this.element);
        }

        this.wireClicks();
        this.element.addEventListener("close", this.onClose, { once: true });

        this.element.showModal();
        document.body.classList.add("w-dialog-open");

        // Trigger the fade-in transition and notify "shown" listeners once the
        // dialog is laid out and visible.
        window.requestAnimationFrame(() =>
        {
            this.element.classList.add("w-dialog-in");
            this.shownCallbacks.forEach(cb => cb());
        });
    }

    hide(): void
    {
        if (this.element.open)
        {
            this.element.classList.remove("w-dialog-in");
            this.element.close();
        }
    }

    onShown(callback: () => void): void
    {
        this.shownCallbacks.push(callback);
    }

    onHidden(callback: () => void): void
    {
        this.element.addEventListener("close", callback);
    }

    remove(): void
    {
        this.element.remove();
    }

    private readonly onClose = () =>
    {
        this.element.classList.remove("w-dialog-in");
        if (!document.querySelector("dialog.w-dialog[open]"))
        {
            document.body.classList.remove("w-dialog-open");
        }
    };

    // A single delegated click handler covers both the close buttons
    // ([data-w-dialog-dismiss], even ones added after show()) and clicks on the
    // backdrop area (a click whose target is the <dialog> element itself).
    private wireClicks(): void
    {
        if (this.clickHandler)
        {
            this.element.removeEventListener("click", this.clickHandler);
        }
        if (this.mouseDownHandler)
        {
            this.element.removeEventListener("mousedown", this.mouseDownHandler);
        }

        // Remember whether the mouse button went down on the backdrop. A text
        // selection that starts inside the dialog and ends on the backdrop fires
        // a "click" whose target is the <dialog> element, which would otherwise
        // close the dialog. We only treat it as a backdrop click when both the
        // press and the release happened on the backdrop.
        this.mouseDownHandler = (e: MouseEvent) =>
        {
            this.mouseDownOnBackdrop = e.target === this.element;
        };
        this.element.addEventListener("mousedown", this.mouseDownHandler);

        this.clickHandler = (e: MouseEvent) =>
        {
            const target = e.target as Element | null;

            if (target && target.closest("[data-w-dialog-dismiss]"))
            {
                this.hide();
                return;
            }

            if (target === this.element && this.mouseDownOnBackdrop && !this.preventBackdropClose)
            {
                this.hide();
            }
        };
        this.element.addEventListener("click", this.clickHandler);
    }
}
